/** The kept views of the summary pages, with the feed entities they depend on (design 16.8). */

import * as api from "../api";
import { PERIOD_DEFAULT } from "../pickupsChart";
import { WEEK_CHOICES } from "../scorecard";
import { note, removeView, viewNames } from "./store";
import { defineView, FALLBACK_POLL_MS, isMounted, VIEW_COLLECTION } from "./views";

/** The feed has only the entity "account"; its triggers map the pickups, visits, routes and tank levels to account ids. */
const ACCOUNT = ["account"] as const;
/** The reports change by the day, not with each tank reading; each reading is an account event. */
const REPORT_MIN_MS = 5 * 60_000;

export const CONTAINERS = defineView<api.ContainerList>({
  name: "containers",
  entities: ACCOUNT,
  module: "tank",
  warm: { key: "containers", load: (token) => api.containerList(token) },
});

export const cycleKey = (week: number | undefined) => `cycle:${week ?? "now"}`;
export const CYCLE = defineView<api.CycleBoard>({
  name: "cycle",
  entities: ACCOUNT,
  module: "route",
  warm: { key: cycleKey(undefined), load: (token) => api.cycleBoard(token) },
});

export const REVIEW = defineView<api.Review>({
  name: "review",
  entities: ACCOUNT,
  module: "review",
  warm: { key: "review", load: (token) => api.review(token) },
});

export const scorecardKey = (weeks: number) => `scorecard:${weeks}`;
export const SCORECARD = defineView<api.Scorecard>({
  name: "scorecard",
  entities: ACCOUNT,
  module: "scorecard",
  minMs: REPORT_MIN_MS,
  warm: { key: scorecardKey(WEEK_CHOICES[0]), load: (token) => api.scorecard(token, WEEK_CHOICES[0]) },
});

export const PARITY = defineView<api.Parity>({
  name: "parity",
  entities: ACCOUNT,
  module: "parity",
  minMs: REPORT_MIN_MS,
  warm: { key: "parity", load: (token) => api.parity(token) },
});

/** A summary of today has no day in its key, so the new day of the org makes it stale and not new. */
export const pickupSummaryKey = (day: string, today: string, weeks: number) => `pickup-summary:${day === today ? "today" : day}:${weeks}`;
export const PICKUP_SUMMARY = defineView<api.PickupsSummary>({
  name: "pickup-summary",
  entities: ACCOUNT,
  module: "review",
  minMs: REPORT_MIN_MS,
  warm: { key: `pickup-summary:today:${PERIOD_DEFAULT}`, load: (token) => api.pickupsSummaryOf(token, undefined, PERIOD_DEFAULT, undefined, true) },
});

export const fillKey = (day: string | undefined) => `fill:${day ?? "today"}`;

const DATED_KEY = /^(?:pickup-summary|fill):(\d{4}-\d{2}-\d{2})(?::|$)/;

/** The day in the key of a dated view, or null for a view of today or with no day. */
export const datedViewDay = (key: string): string | null => DATED_KEY.exec(key)?.[1] ?? null;

/** Remove the kept views of the days before `today`, except a view that a page shows; returns the count. */
export function dropPastViews(today: string, reason: string): number {
  const started = performance.now();
  const past = viewNames(VIEW_COLLECTION).filter((key) => {
    const day = datedViewDay(key);
    return day !== null && day < today && !isMounted(key);
  });
  const count = past.filter((key) => removeView(key)).length;
  const ms = Math.round(performance.now() - started);
  if (count) note("view_dropped", `views: removed ${count} views of past days (${reason}) in ${ms} ms`, { views: count, reason, ms });
  return count;
}
export const FILL = defineView<api.FillDay>({
  name: "fill",
  entities: ACCOUNT,
  module: "fill_view",
  minMs: REPORT_MIN_MS,
  warm: { key: fillKey(undefined), load: (token) => api.fillDay(token) },
});

/** The home page of the office roles: at most as often as its old poll, because each tank reading makes it stale. */
export const OVERVIEW = defineView<api.Overview>({
  name: "overview",
  entities: ACCOUNT,
  module: "review",
  minMs: FALLBACK_POLL_MS,
  warm: { key: "overview", load: (token) => api.overview(token) },
});

/** The account pins with no filter, and the box of the map that they are for; null: all pins, for the first view. */
export interface KeptPins {
  box: string | null;
  list: api.PinList;
}
export const pinsKey = (layer: api.PinLayer) => `pins:${layer}`;
export const PINS = defineView<KeptPins>({
  name: "pins",
  entities: ACCOUNT,
  module: "account",
  warm: { key: pinsKey("door"), load: async (token) => ({ box: null, list: await api.mapPins(token, "accounts", "door", {}, null) }) },
});
